// Plain-fetch OIDC primitives. No client library — the contract surface is
// authorize + token + end_session and the wire format is well-known.

export interface BuildAuthorizeUrlInput {
  issuer: string;
  clientId: string;
  redirectUri: string;
  state: string;
  codeChallenge: string;
  scope: string;
  loginHint?: string;
}

export function buildAuthorizeUrl(input: BuildAuthorizeUrlInput): string {
  // Zitadel v4 discovery doc advertises `/oauth/v2/authorize`; legacy
  // `/oidc/v1/authorize` returns 404 (verified 2026-05-07 against the
  // local stack). `end_session` keeps its `/oidc/v1/` path because the
  // discovery doc explicitly advertises that one as v1.
  const url = new URL("/oauth/v2/authorize", input.issuer);
  url.searchParams.set("client_id", input.clientId);
  url.searchParams.set("redirect_uri", input.redirectUri);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("scope", input.scope);
  url.searchParams.set("state", input.state);
  url.searchParams.set("code_challenge", input.codeChallenge);
  url.searchParams.set("code_challenge_method", "S256");
  if (input.loginHint) url.searchParams.set("login_hint", input.loginHint);
  return url.toString();
}

export function buildEndSessionUrl(input: {
  issuer: string;
  postLogoutRedirectUri: string;
  idTokenHint?: string;
}): string {
  const url = new URL("/oidc/v1/end_session", input.issuer);
  url.searchParams.set("post_logout_redirect_uri", input.postLogoutRedirectUri);
  if (input.idTokenHint)
    url.searchParams.set("id_token_hint", input.idTokenHint);
  return url.toString();
}

export interface OidcTokenResponse {
  access_token: string;
  refresh_token: string;
  id_token?: string;
  token_type: string;
  expires_in: number;
}

export async function exchangeCodeForTokens(input: {
  issuer: string;
  code: string;
  codeVerifier: string;
  redirectUri: string;
  clientId: string;
}): Promise<OidcTokenResponse> {
  const body = new URLSearchParams();
  body.set("grant_type", "authorization_code");
  body.set("code", input.code);
  body.set("redirect_uri", input.redirectUri);
  body.set("client_id", input.clientId);
  body.set("code_verifier", input.codeVerifier);

  const res = await fetch(new URL("/oauth/v2/token", input.issuer).toString(), {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
  if (!res.ok) {
    throw new Error(`OIDC token exchange failed: ${String(res.status)}`);
  }
  return (await res.json()) as OidcTokenResponse;
}

export async function refreshAccessToken(input: {
  issuer: string;
  refreshToken: string;
  clientId: string;
}): Promise<OidcTokenResponse> {
  const body = new URLSearchParams();
  body.set("grant_type", "refresh_token");
  body.set("refresh_token", input.refreshToken);
  body.set("client_id", input.clientId);

  const res = await fetch(new URL("/oauth/v2/token", input.issuer).toString(), {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
  if (!res.ok) {
    throw new Error(`OIDC refresh failed: ${String(res.status)}`);
  }
  return (await res.json()) as OidcTokenResponse;
}
